// =============================================================================
// ui/format.ts — number and money formatting, ported literally.
// -----------------------------------------------------------------------------
// PRESENTATION ONLY. Nothing here derives a value; every function takes a number
// that `domain/` already computed and turns it into the string the old client
// shows. That is why it lives in `ui/` rather than behind a selector.
//
// ⚠ PORTED LITERALLY, because these are parity surface. The first version of the
// Trades tab used `toLocaleString({ style: 'currency' })`, which renders $50 as
// "$50.00" — while the old client renders "+$50.00". Every P&L cell on every
// screen differed by a character, which is exactly the kind of difference a
// source-read cannot see and a side-by-side screenshot shows immediately.
//
// The em-dash matters as much as the sign. A null P&L is an OPEN or unpriced
// trade — no result yet — and rendering that as "$0.00" makes it read as a
// scratch, which is a different and confident claim.
// =============================================================================

import type { PnlUnit } from '../selectors/pnlUnit';

/** `+$1,234.56` / `-$80.00` / `$0.00` / `—`. The sign prefix is the old client's. */
export function money(n: number | null | undefined, decimals = 2): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const sign = v < 0 ? '-' : v > 0 ? '+' : '';
  return (
    sign +
    '$' +
    Math.abs(v).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  );
}

/** Unsigned money, for costs. Commissions are never shown as "+$2.00". */
export function moneyPlain(n: number | null | undefined, decimals = 2): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return (
    '$' +
    Number(n).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  );
}

/**
 * `12.50`, `1,234.00`, `—`.
 *
 * ⚠ `Number.isFinite`, not `isNaN`. `isNaN(Infinity)` is FALSE, so an upstream
 * divide-by-zero would render a literal "∞" into a numeric column — the old
 * client hit exactly that and fixed it the same way.
 */
export function num(n: number | null | undefined, decimals = 2): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** `62%`, `61.5%`, `—`. */
export function pct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return Number(n).toFixed(decimals) + '%';
}

/**
 * The P&L colour class: `green` / `red` / `dim`.
 *
 * ⚠ Zero is `dim`, not green. A scratch is not a win, and colouring it as one
 * inflates how a flat day reads at a glance.
 */
export function pnlClass(n: number | null | undefined): 'green' | 'red' | 'dim' {
  const v = Number(n);
  if (!Number.isFinite(v)) return 'dim';
  return v > 0 ? 'green' : v < 0 ? 'red' : 'dim';
}

/**
 * Green above zero, red below, and NO CLASS AT ZERO.
 *
 * ⚠ NOT `pnlClass`, and the difference is one word. That one returns `dim` for
 * zero, which greys the figure; beta leaves these particular cards' zero at the
 * default colour. Both are deliberate and they are not interchangeable — using
 * `pnlClass` here would grey a break-even pattern's expectancy where beta does
 * not.
 *
 * 🚨 IT LIVES HERE BECAUSE TWO CARDS HAD DRIFTED. The pattern card tested
 * `> 0 / < 0 / else`, its exit-model sibling sixty lines below tested `>= 0` —
 * so a model that had made exactly nothing read as a winner in green. A rule
 * spelled out twice is a rule that will be spelled out differently.
 */
export function threeWay(v: number): string {
  return v > 0 ? 'green' : v < 0 ? 'red' : '';
}

/**
 * The stored clock time, rendered as stored.
 *
 * ── 🚨 IT USED TO TRUNCATE TO `HH:MM`, AND THAT WAS INVENTED ──
 *
 * The old docstring read *"Seconds are noise in a table; the detail view keeps
 * them"* — a product judgment this port made up. The old client never truncates
 * `timeIn`/`timeOut` for display anywhere; it renders the stored value, and its
 * Trades table shows `10:35:28`. The only `.slice()` on a time in that file
 * extracts the HOUR for session bucketing, not for display.
 *
 * ⚠ And the judgment was wrong on its own terms. Josh scalps: his book carries
 * 10:33:13 and 10:33:56 as two SEPARATE trades, and at minute resolution they
 * render as one repeated row — which is exactly what a duplicate looks like.
 * After a session spent proving nine real duplicate pairs are in this book, a
 * formatter that manufactures the appearance of more is the wrong default.
 *
 * ⚠ Pass-through, not reformat: a legacy row storing `09:31` with no seconds
 * still renders `09:31`, which is what the old client shows for it too.
 */
export function clock(t: string | null | undefined): string {
  return t ? String(t) : '';
}

/**
 * `2026-07-08` → `Wed, 7/8`. The old client's carried-forward focus heading.
 *
 * ⚠ A bare ISO date under that heading is a worse label than it looks: the whole
 * point of "carried from" is how long ago the thought was, and a weekday answers
 * that at a glance where `2026-07-08` does not.
 *
 * ⚠ `T00:00:00Z` PAIRED WITH `timeZone: 'UTC'`. These are calendar labels with no
 * instant behind them, so the parse and the render have to agree about the zone —
 * mixing a UTC parse with local rendering shows the previous day for anyone west
 * of Greenwich, which is most users. The old client pairs local with local; this
 * pairs UTC with UTC and produces the same string without depending on where the
 * reader is.
 */
export function dayLabel(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short', month: 'numeric', day: 'numeric', timeZone: 'UTC',
  });
}

/**
 * `2026-08-01` → `Saturday, August 1, 2026`. The dashboard greeting's subtitle.
 *
 * The old client writes this one inline, straight off the wall clock:
 *
 *     new Date().toLocaleDateString('en-US',
 *       { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
 *
 * ⚠ The FORMAT is copied and the VALUE deliberately is not. Beta formats
 * `new Date()`; this formats the range's `today`, which in session mode is the
 * SESSION day. They agree on every calendar day and diverge on exactly one kind
 * of evening — Sunday 20:00 ET opens Monday's session — and there the session
 * day is the right answer, because the widget beside this line counts "trades
 * today" over that same session day. A subtitle reading Sunday above a count
 * scoped to Monday is two claims about one word.
 *
 * ⚠ Same `T00:00:00Z` + `timeZone: 'UTC'` pairing as `dayLabel`, and for the same
 * reason: a UTC parse rendered in local time shows the previous day for every
 * reader west of Greenwich.
 */
export function fullDayLabel(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}

/**
 * `$1,000` — unsigned, and no forced decimals.
 *
 * ⚠ A THIRD money formatter, and it earns its place: the glance band and the
 * contract card print a TARGET, not a result. `money()` renders 1000 as
 * "+$1,000.00", and a plus sign on a goal reads as a gain already made. The old
 * client uses a bare `'$' + n.toLocaleString()` at exactly these call sites.
 */
export function moneyTarget(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return '$' + Number(n).toLocaleString('en-US');
}

/**
 * One custom-widget number, formatted by its metric's unit.
 *
 * ⚠ THE EM-DASH IS LOAD-BEARING HERE MORE THAN ANYWHERE. `Win/Loss ratio` on a
 * clean week and `Avg risk` where nothing carries a risk figure both return
 * null, and both mean "there is nothing to divide" — not zero. A widget that
 * printed `0.00×` for a week with no losers would be reporting a total collapse
 * on the trader's best week.
 *
 * ⚠ `$u` IS UNSIGNED. Commissions and risk are costs; the trader paid $240, so
 * it reads `$240`, never `-$240`.
 *
 * ⚠ WHOLE DOLLARS. `money(v, 0)`, not the default two places — these are axis
 * ticks and legends on a 600-unit-wide chart, and `+$3,046.00` does not fit.
 *
 * The `−` before an R and the `×` after a ratio are the old client's glyphs
 * (U+2212 and U+00D7), not a hyphen and an x.
 */
export function metricValue(v: number | null | undefined, unit: string): string {
  // ⚠ `Number.isFinite`, where beta uses `isNaN` — same divergence, same reason,
  // as `num()` above. Nothing in the metric catalogue can currently return
  // Infinity, so this changes no rendered value; it just refuses to print `$∞`
  // if one ever does.
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (unit === '$') return money(n, 0);
  if (unit === '$u') return '$' + Math.round(Math.abs(n)).toLocaleString('en-US');
  if (unit === '%') return n.toFixed(0) + '%';
  if (unit === '#') return String(Math.round(n));
  if (unit === 'min') return Math.round(n) + 'm';
  if (unit === 'R') return (n >= 0 ? '+' : '−') + Math.abs(n).toFixed(2) + 'R';
  if (unit === 'x') return n.toFixed(2) + '×';
  return String(Math.round(n * 100) / 100);
}

/**
 * One P&L figure in the trader's chosen unit.
 *
 * ── PORTED FROM BETA'S `fmtPnlUnit`, INCLUDING THE PARTS THAT LOOK ARBITRARY ──
 *
 * `+$1,234.56` · `+82.50 pts` · `-14.0 ticks` · `+1.75R` · `—`
 *
 * ⚠ DOLLARS ROUTES THROUGH `money()` AND NOTHING ELSE. That keeps the default
 * view byte-identical to every screen already signed off, so turning this on
 * cannot change what a dollars user sees.
 *
 * ⚠ THE EM-DASH IS THE WHOLE POINT OF THE NULL CASE, and it carries more weight
 * here than anywhere else in this file. `null` is what `tradeValue` returns when
 * a figure genuinely does not exist in the chosen unit — R on a trade that had
 * no stop, ticks on a contract missing from the catalogue. Rendering that as
 * `0.00R` would not be a formatting slip; it would claim the trade broke even.
 *
 * ⚠ `tick` / `ticks` IS SINGULARISED, and beta does it on the ABSOLUTE value, so
 * -1 reads `-1 tick`. Points and R are not singularised — `1 pts` and `1.00R`
 * are what beta prints.
 *
 * ⚠ DEFAULT PRECISION DIFFERS PER UNIT: 2 for dollars, points and R, but 1 for
 * ticks. A tick is already the smallest increment the market trades in, so a
 * second decimal is noise on a quantity that cannot be fractional in practice.
 */
export function unitValue(
  v: number | null | undefined,
  unit: PnlUnit,
  decimals?: number,
): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (unit === 'usd') return money(n, decimals ?? 2);
  const sign = n < 0 ? '-' : n > 0 ? '+' : '';
  const abs = Math.abs(n);
  if (unit === 'r') return sign + abs.toFixed(decimals ?? 2) + 'R';
  if (unit === 'ticks') {
    return sign + abs.toLocaleString('en-US', {
      minimumFractionDigits: 0, maximumFractionDigits: decimals ?? 1,
    }) + (abs === 1 ? ' tick' : ' ticks');
  }
  return sign + abs.toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: decimals ?? 2,
  }) + ' pts';
}

/**
 * The dense-cell variant — calendar days, breakdown tables.
 *
 * ⚠ WHOLE DOLLARS BUT NATURAL PRECISION EVERYWHERE ELSE, which is beta's
 * `fmtPnlCompact` and is not an inconsistency. `-$3,185` instead of
 * `-$3,185.00` is what makes a calendar cell fit; but an R value rounded to
 * whole numbers is `+2R` for anything from 1.5 to 2.49, which is most of the
 * information in the number gone.
 */
export function unitCompact(v: number | null | undefined, unit: PnlUnit): string {
  return unitValue(v, unit, unit === 'usd' ? 0 : undefined);
}

/**
 * A P&L magnitude in `unit`, UNSIGNED — the `moneyPlain` of the unit family.
 *
 * ── 🚨 WHY THIS EXISTS, WHICH IS A BUG IT ALREADY CAUGHT ──
 *
 * `unitValue` signs everything, because a standalone P&L figure has to say
 * which way it went. Some figures are COSTS, and the sentence around them
 * already supplies the direction: "giving back +$50 per trade" and "your losers
 * (+$100)" both read as gains in the middle of a sentence explaining a loss.
 *
 * The dollar half of this family has always had `moneyPlain` for the same
 * reason — "Commissions are never shown as +$2.00". Converting those call sites
 * to `unitValue` reintroduced the sign, and two Edge Health tests that pin the
 * exact prose went red.
 *
 * ⚠ It does NOT take an absolute value. The caller decides what magnitude it is
 * describing; this only declines to prefix a sign, so a caller passing a
 * negative still gets `-` and cannot accidentally claim a loss was a gain.
 */
export function unitPlain(
  v: number | null | undefined,
  unit: PnlUnit,
  decimals?: number,
): string {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (unit === 'usd') return moneyPlain(n, decimals ?? 0);
  // Reuse the signed formatter and drop a leading `+`; a `-` is kept.
  return unitValue(n, unit, decimals).replace(/^\+/, '');
}
